import * as React from 'react';
import { DatePicker, DayOfWeek, IDatePickerStrings, IDatePicker, ITextField } from 'office-ui-fabric-react';
import { IFormInputProps } from '../../common/interfaces/IFormInputProps'
import InputErrorMessage from "./inputErrorMessage";
import { IFormInputComponent } from "../interfaces/IFormInputComponent";
import { Validator, ValidatorType } from "../../../validation";
import { MaybeDate } from '../../../entities/common';
import { FormatDate, FormatUniversalDateTime, MaxDateConst, MinDateConst, cultureInfo, toDate } from '../../utils/common';
import { DateTime } from 'luxon';
import ClearInput from './TextInput/ClearInput';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { getFirstDayOfWeekSetting } from '../../../store/selectors/settingsSelectors';
import ShowTimeContext from '../ShowTimeContext';

export const DayPickerStrings: IDatePickerStrings = {
    months: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
    ],

    shortMonths: [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
    ],

    days: [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday'
    ],

    shortDays: [
        'S',
        'M',
        'T',
        'W',
        'T',
        'F',
        'S'
    ],

    goToToday: 'Go to today',
    prevMonthAriaLabel: 'Go to previous month',
    nextMonthAriaLabel: 'Go to next month',
    prevYearAriaLabel: 'Go to previous year',
    nextYearAriaLabel: 'Go to next year',

    isRequiredErrorMessage: 'This field is required',
    isOutOfBoundsErrorMessage: ' ',
    invalidInputErrorMessage: 'Invalid date format'
};

export type DatePickerInputProps = {
    minDate?: MaybeDate;
    maxDate?: MaybeDate;
    restrictedDates?: Date[];
    onParseDateFromString?: (v: string) => void;
    disableAutoFocus?: boolean;
    isRequired?: boolean;
    isClearable?: boolean;
    showTime?: boolean;
} & IFormInputProps<string>;

type DatePickerInputState = {
    firstDayOfWeek?: DayOfWeek;
    value?: Date;
    minDate?: Date;
    maxDate?: Date;
}

type StateProps = {
    firstDayOfWeek: DayOfWeek;
}

type Props = DatePickerInputProps & StateProps;

class DatePickerInput extends React.Component<Props, DatePickerInputState> implements IFormInputComponent {
    static readonly contextType = ShowTimeContext;
    declare context: React.ContextType<typeof ShowTimeContext>;
    private inputComponent = React.createRef<IDatePicker>();
    private textFieldComponent = React.createRef<ITextField>();

    componentWillMount() {
        this.setState({
            value: this.props.value ? toDate(this.props.value) : undefined,
            minDate: this._getMinDate(this.props.minDate),
            maxDate: this._getMaxDate(this.props.maxDate),
            firstDayOfWeek: this.props.firstDayOfWeek,
        });
    }

    private _getMaxDate(newDate: MaybeDate) {
        let maxDate = newDate ? toDate(newDate) : undefined;
        if (!maxDate || maxDate > MaxDateConst) {
            maxDate = MaxDateConst;
        }
        return maxDate;
    }

    private _getMinDate(newDate: MaybeDate) {
        let minDate = newDate ? toDate(newDate) : undefined;
        if (!minDate || minDate < MinDateConst) {
            minDate = MinDateConst;
        }
        return minDate;
    }

    componentDidMount() {
        this.props.inputRef && this.props.inputRef(this);
    }

    componentWillReceiveProps(nextProps: Props) {
        if (this.props.value !== nextProps.value) {
            const newValue = nextProps.value ? toDate(nextProps.value) : undefined;
            this.setState({ value: newValue });
        }
        const isDate = Validator.new().date().build();
        if (this.props.minDate !== nextProps.minDate && isDate.isValid(this.state.minDate)) {
            const newMinDateValue = this._getMinDate(nextProps.minDate);
            this.setState({ minDate: newMinDateValue });
        }
        if (this.props.maxDate !== nextProps.maxDate && isDate.isValid(this.state.maxDate)) {
            const newMaxDateValue = this._getMaxDate(nextProps.maxDate);
            this.setState({ maxDate: newMaxDateValue });
        }
        if (this.props.firstDayOfWeek !== nextProps.firstDayOfWeek) {
            this.setState({ firstDayOfWeek: nextProps.firstDayOfWeek });
        }
    }

    public render() {
        const { firstDayOfWeek, value, minDate, maxDate } = this.state;
        const { inputProps, disabled, readOnly, disableAutoFocus, isRequired, restrictedDates } = this.props;
        const isReadOnly = readOnly || inputProps?.readOnly;
        const isDisabled = disabled || inputProps?.disabled;

        const isClearable = !isRequired && !isReadOnly && !isDisabled && !this.props.validator?.has(ValidatorType.required);

        return <>
            <DatePicker
                placeholder={inputProps?.placeholder}
                componentRef={this.inputComponent}
                firstDayOfWeek={firstDayOfWeek}
                strings={DayPickerStrings}
                onSelectDate={this._onSelectDate}
                formatDate={this._buildFormatDate()}
                parseDateFromString={this._onParseDateFromString}
                allowTextInput
                textField={{
                    readOnly: isReadOnly,
                    disabled: isDisabled
                        ? true
                        : isReadOnly
                            ? false
                            : undefined,
                    className: isClearable ? "clearable" : undefined,
                    onRenderSuffix: value && isClearable ? () => <ClearInput onClick={() => {
                        this._onSelectDate(undefined);
                        this.textFieldComponent.current?.blur();
                        this.inputComponent.current?.reset();
                    }} /> : undefined,
                    componentRef: this.textFieldComponent
                }}
                calendarProps={{ strings: DayPickerStrings, restrictedDates }}
                value={value}
                minDate={isReadOnly || isDisabled ? undefined : minDate}
                maxDate={isReadOnly || isDisabled ? undefined : maxDate}
                disabled={isReadOnly || isDisabled}
                disableAutoFocus={disableAutoFocus}
                className={this._validate(value) ? undefined : "invalid"}
                title={this.props.inputProps?.title}
                isRequired={isRequired} />
            <InputErrorMessage text={this._getErrorMessage(value)} onErrorRender={this.props.onErrorRender} />
        </>
    }

    private _onSelectDate = (date: Date | null | undefined): void => {
        if (date === null) {
            date = undefined;
        }
        if (this.state.value === date) {
            return;
        }

        this.setState({ value: date });

        const value = date?.toDateOnlyString() ?? null;
        this.props.onChanged?.(value);
        this._validate(date) && this.props.onEditComplete?.(value);

        this.inputComponent.current?.focus();
    }

    private _shouldShowTime = (): boolean => !!this.props.showTime && !!this.context;

    // Fluent's DatePicker compares its `value` prop ignoring time-of-day, so it skips updating
    // the displayed text when only the time changes. Returning a fresh formatDate reference keyed
    // by props.value bypasses that check and forces the text to refresh.
    private _formatDateKey: any = undefined;
    private _formatDateFn: ((date?: Date) => string) | undefined = undefined;
    private _buildFormatDate = (): (date?: Date) => string => {
        if (this._formatDateKey !== this.props.value || !this._formatDateFn) {
            this._formatDateKey = this.props.value;
            this._formatDateFn = (d?: Date) => this._onFormatDate(d);
        }
        return this._formatDateFn;
    }

    private _onFormatDate = (date?: Date): string => {
        if (date && date instanceof Date && !isNaN(date.getTime())) {
            if (this._shouldShowTime() && typeof this.props.value === 'string' && this.props.value.includes('T')) {
                return FormatUniversalDateTime(this.props.value, true) ?? '';
            }
            return FormatDate(date, { year: 'numeric', month: 'numeric', day: 'numeric'})!;
        }
        return '';
    }

    private _onParseDateFromString = (value: string): Date | null => {
        this.props.onParseDateFromString?.(value);

        const format = DateTime.parseFormatForOpts(DateTime.DATE_SHORT, { locale: cultureInfo.name });
        let dateTime = DateTime.fromFormat(value, format!);

        if (!dateTime.isValid && this._shouldShowTime()) {
            const datePart = value.split(',')[0]?.trim();
            if (datePart) {
                dateTime = DateTime.fromFormat(datePart, format!);
            }
        }

        return dateTime.isValid ? dateTime.toJSDate() : null;
    }

    private _validate = (value?: Date) => {
        return this.props.validator === undefined || this.props.validator.isValid(value);
    }

    private _getErrorMessage = (value?: Date) => {
        return this.props.validator && this.props.validator.getErrorMessage(value);
    }

    focus(): void {
        (this.inputComponent.current as any)._onTextFieldClick();
    }

    focusDatepicker(): void {
        this.inputComponent.current?.focus();
    }
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    firstDayOfWeek: getFirstDayOfWeekSetting(state)
});

export default connect(mapStateToProps, null, null, { withRef: true })(DatePickerInput);